import { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { newsService } from '../../../services/news.service.js';
import { useAuth } from '../../../context/AuthContext';
import { shortenEmail } from '../../../utils/email';
import Loader from '../../common/Loader/Loader';
import { RotateCcw, Search, User, Link } from 'lucide-react';
import './ArchivedNews.css';

const ArchivedNewsCard = ({ news, onUnarchive }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd/MM/yy • HH:mm');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'dd/MM/yy • HH:mm');
  };

  const CATEGORY_IMAGES = {
    National: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451199_757.jpg',
    Business: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446926_903.jpg',
    Sports: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447376_94.jpg',
    World: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322446584_697.jpg',
    Politics: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322451612_74.jpg',
    Technology: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
    Startup: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322452049_264.jpg',
    Entertainment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448662_37.jpg',
    Miscellaneous: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg',
    Hatke: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450829_392.jpg',
    Science: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322450268_226.jpg',
    Automobile: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449000_634.jpg',
    Education: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449426_933.jpg',
    Environment: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322445045_502.jpg',
    Travel: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322448299_383.jpg',
    Fashion: 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322447765_483.jpg',
  };

  const DEFAULT_IMAGE = 'https://media.inshorts.com/inshorts/images/v1/variants/jpg/m/2026/09_sep/25_fri/img_1790322449929_664.jpg';

  const getNewsImage = (news) => {
    if (news.imageUrl) return news.imageUrl;
    return CATEGORY_IMAGES[news.category] || DEFAULT_IMAGE;
  };

  return (
    <div className="archived-news-card">
      <div className="archived-news-card-image">
        <img src={getNewsImage(news)} alt={news.headline} loading="lazy" />
        <div className="archived-news-overlay"></div>

        {/* Unarchive button */}
        <button
          className="unarchive-btn"
          onClick={(e) => {
            e.stopPropagation();
            onUnarchive(news._id);
          }}
          title="Unarchive"
        >
          <RotateCcw size={16} />
          <span>Restore</span>
        </button>

        {/* Status badge */}
        <span className="archived-news-status-badge">ARCHIVED</span>

        {/* Categories */}
        {news.categories && news.categories.length > 0 ? (
          <span className="archived-news-categories">
            {news.categories.join(' • ')}
          </span>
        ) : (
          <span className="archived-news-categories">{news.category}</span>
        )}
      </div>

      <div className="archived-news-card-content">
        <h3 className="archived-news-card-headline">{news.headline}</h3>
        {news.summary && (
          <p className="archived-news-card-summary">{news.summary}</p>
        )}
      </div>

      <div className="archived-news-card-footer">
        <div className="archived-news-footer-top">
          <span className="archived-news-date">
            Archived: {formatDateTime(news.createdAt)}
          </span>
          <span className="archived-news-archived-date">
            {formatDateTime(news.archivedAt)}
          </span>
        </div>
        <div className="archived-news-footer-bottom">
          <span className="archived-news-user" title="Archived by">
            <User size={12} />
            <span>{shortenEmail(news.archivedBy || news.userId?.email || 'Unknown')}</span>
          </span>
          {news.sourceUrl && (
            <a
              href={news.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="archived-news-footer-source"
              title="Open source"
              onClick={(e) => e.stopPropagation()}
            >
              <Link size={12} />
              <span>Source</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const ArchivedNews = () => {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const loaderRef = useRef(null);
  const fetchInProgressRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchArchivedNews = async (pageNum = 1, isLoadMore = false) => {
    if (fetchInProgressRef.current) {
      return;
    }

    fetchInProgressRef.current = true;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setPageLoading(true);
    }
    setError(null);

    try {
      let response;
      if (isAdmin) {
        response = await newsService.getAllArchivedNews({
          search: searchQuery.trim(),
          page: pageNum,
        });
      } else {
        response = await newsService.getArchivedNews({
          search: searchQuery.trim(),
          page: pageNum,
        });
      }

      if (response.success || response.data) {
        if (isLoadMore) {
          setNews((prev) => [...prev, ...(response.data || response.news || [])]);
        } else {
          setNews(response.data || response.news || []);
        }
        setHasMore((response.pagination?.totalPages || 0) > pageNum);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch archived shorts');
    } finally {
      fetchInProgressRef.current = false;
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
        setPageLoading(false);
      }
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
    setHasMore(true);
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchArchivedNews(1, false);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !pageLoading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchArchivedNews(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, pageLoading, loadingMore, page]);

  const handleUnarchive = async (newsId) => {
    if (!confirm('Are you sure you want to unarchive this shorts?')) return;

    try {
      await newsService.unarchiveNews(newsId);
      setNews(news.filter((n) => n._id !== newsId));
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to unarchive shorts';
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="archived-news-container">
        <div className="archived-news-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="archived-news-container">
      {/* Search Bar - Only show if there are archived news */}
      {news.length > 0 && (
        <div className="archived-news-search">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search archived shorts..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="archived-news-error">
          {error}
          <button onClick={() => fetchArchivedNews()} className="archived-news-retry">
            Retry
          </button>
        </div>
      )}

      {news.length === 0 && !error ? (
        <div className="archived-news-empty">
          <div className="archived-news-empty-icon">📦</div>
          <h3>No archived shorts</h3>
          <p>Shorts you archive will appear here.</p>
        </div>
      ) : (
        <div className="archived-news-grid">
          {news.map((item) => (
            <ArchivedNewsCard
              key={item._id}
              news={item}
              onUnarchive={handleUnarchive}
            />
          ))}
          {/* Infinite scroll loader */}
          {hasMore && (
            <div ref={loaderRef} className="infinite-scroll-loader">
              {loadingMore && <Loader />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArchivedNews;
